import {
  buildPath,
  buildTemplatePath,
  Grid,
  newToast,
  Page,
  PageRoute,
  useElementBreakpoints,
  useValidationErrors,
} from "@compass/core";
import {
  Contributor,
  MatchReportActualMatch,
  MatchReportActualMatchFilter,
  MatchReportActualYearly,
  MatchReportActualYearlyFilter,
  MatchReportAllPartners,
  MatchReportAllPartnersFilter,
  MatchReportContributorDetail,
  MatchReportContributorDetailFilter,
  MatchReportContributorFilter,
  MatchReportContributorSummary,
  MatchReportContributorSummaryFilter,
  MatchReportNonPersonnelDetail,
  MatchReportNonPersonnelDetailFilter,
  MatchReportPartnerFilter,
  MatchReportTimeCardFilter,
} from "generated/openApi";
import "moment-timezone";
import { useState } from "react";

import ReactDOMServer from "react-dom/server";
import { useDispatch } from "react-redux";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { ContributorHtml } from "./Contributor/ContributorHtml";
import { GeneratedReport } from "./GeneratedReport";
import { MatchReportPageFilters } from "./MatchReportPageFilters";
import { MatchReportPageHeader } from "./MatchReportPageHeader";
import { PartnerHtml } from "./Partner/PartnerHtml";
import { TimeCardHtml } from "./TimeCard/TimeCardHtml";

const routeName = "matchReport";

export const matchReportPageRoute: PageRoute = {
  handle: "Reports",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type MatchReportType =
  | "Contributor"
  | "Contributor Summary"
  | "Contributor Details"
  | "Contributor Details"
  | "Non-Personnel Details"
  | "Partner"
  | "All Partners"
  | "Time Card"
  | "Actual Match"
  | "Actual Yearly";

export type MatchReportSelection = {
  name: MatchReportType;
  generateTypes: ("on-screen" | "pdf" | "direct-pdf" | "excel")[];
};

export type MatchReportGlobalFilter = {
  dateFrom?: Date;
  dateTo?: Date;
  contributorId?: number | undefined;
  contributor?: Contributor | undefined;
  partnerIds?: number[] | undefined;
  subPartnerIds?: number[] | undefined;
  reviewId?: number | undefined;
  isMatch?: boolean | undefined;
};

export const MatchReportPage = () => {
  const dispatch = useDispatch();
  const { breakpointsRef, setBreakpoints } = useElementBreakpoints();
  const [selectedReportType, setSelectedReportType] = useState<MatchReportType>();
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [filtersCollapsed, setFilterCollapsed] = useState<boolean>(false);

  // this filter helps maintain filters for certain while toggling between reports
  const [globalFilter, setGlobalFilter] = useState<MatchReportGlobalFilter>({});

  const reportTypes: MatchReportSelection[] = [
    { name: "Actual Match", generateTypes: ["on-screen", "pdf", "excel"] },
    { name: "Actual Yearly", generateTypes: ["on-screen", "pdf", "excel"] },
    { name: "All Partners", generateTypes: ["on-screen", "pdf", "excel"] },
    { name: "Contributor", generateTypes: ["direct-pdf"] },
    { name: "Contributor Details", generateTypes: ["on-screen", "pdf", "excel"] },
    { name: "Contributor Summary", generateTypes: ["on-screen", "pdf", "excel"] },
    { name: "Non-Personnel Details", generateTypes: ["on-screen", "pdf", "excel"] },
    { name: "Partner", generateTypes: ["direct-pdf"] },
    { name: "Time Card", generateTypes: ["direct-pdf"] },
  ];

  // report filters & results
  const [contributorFilter, setContributorFilter] = useState<MatchReportContributorFilter>(
    {} as MatchReportContributorFilter
  );

  const [contributorSummary, setContributorSummary] = useState<MatchReportContributorSummary>();
  const [contributorSummaryFilter, setContributorSummaryFilter] = useState<MatchReportContributorSummaryFilter>(
    {} as MatchReportContributorSummaryFilter
  );

  const [nonPersonnelDetail, setNonPersonnelDetail] = useState<MatchReportNonPersonnelDetail>();
  const [nonPersonnelDetailFilter, setNonPersonnelDetailFilter] = useState<MatchReportNonPersonnelDetailFilter>(
    {} as MatchReportNonPersonnelDetailFilter
  );

  const [contributorDetail, setContributorDetail] = useState<MatchReportContributorDetail>();
  const [contributorDetailFilter, setContributorDetailFilter] = useState<MatchReportContributorDetailFilter>(
    {} as MatchReportContributorDetailFilter
  );

  const [partnerFilter, setPartnerFilter] = useState<MatchReportPartnerFilter>({} as MatchReportPartnerFilter);

  const [allPartners, setAllPartners] = useState<MatchReportAllPartners>();
  const [allPartnersFilter, setAllPartnersFilter] = useState<MatchReportAllPartnersFilter>(
    {} as MatchReportAllPartnersFilter
  );

  const [timeCardFilter, setTimeCardFilter] = useState<MatchReportTimeCardFilter>({} as MatchReportTimeCardFilter);

  const [actualMatch, setActualMatch] = useState<MatchReportActualMatch>();
  const [actualMatchFilter, setActualMatchFilter] = useState<MatchReportActualMatchFilter>(
    {} as MatchReportActualMatchFilter
  );

  const [actualYearly, setActualYearly] = useState<MatchReportActualYearly>();
  const [actualYearlyFilter, setActualYearlyFilter] = useState<MatchReportActualYearlyFilter>(
    {} as MatchReportActualYearlyFilter
  );

  const generateReport = () => {
    switch (selectedReportType) {
      case "Contributor Summary":
        generateContributorSummaryReport(contributorSummaryFilter);
        break;
      case "Contributor Details":
        generateContributorDetailReport(contributorDetailFilter);
        break;
      case "Non-Personnel Details":
        generateNonPersonnelDetailReport(nonPersonnelDetailFilter);
        break;
      case "Contributor":
        generateContributorReport(contributorFilter);
        break;
      case "Partner":
        generatePartnerReport(partnerFilter);
        break;
      case "All Partners":
        generateAllPartnersReport(allPartnersFilter);
        break;
      case "Time Card":
        generateTimeCardReport(timeCardFilter);
        break;
      case "Actual Match":
        generateActualMatchReport(actualMatchFilter);
        break;
      case "Actual Yearly":
        generateActualYearlyReport(actualYearlyFilter);
        break;
    }
  };

  const generateContributorReport = (filter: MatchReportContributorFilter) => {
    setValidationErrors({});

    ApiService.matchReports
      .generateContributor(filter)
      .then((response) => {
        let htmlString = ReactDOMServer.renderToString(<ContributorHtml report={response} />);
        ApiService.common
          .generatePdfFromHtml({ html: htmlString, filename: "MatchReportContributor.pdf", landscape: false })
          .then((response) => {
            if (response) {
              var file = new Blob([response.data], { type: "application/pdf" });
              var fileURL = URL.createObjectURL(file);
              window.open(fileURL);
            }
          });
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  const generateContributorDetailReport = (filter: MatchReportContributorDetailFilter) => {
    setValidationErrors({});

    ApiService.matchReports
      .generateContributorDetail(filter)
      .then((response) => {
        dispatch(setAppToast(newToast("success")));
        setContributorDetail(response);
        setFilterCollapsed(true);
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  const generateNonPersonnelDetailReport = (filter: MatchReportNonPersonnelDetailFilter) => {
    setValidationErrors({});

    ApiService.matchReports
      .generateNonPersonnelDetail(filter)
      .then((response) => {
        dispatch(setAppToast(newToast("success")));
        setNonPersonnelDetail(response);
        setFilterCollapsed(true);
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  const generateContributorSummaryReport = (filter: MatchReportContributorSummaryFilter) => {
    setValidationErrors({});

    ApiService.matchReports
      .generateContributorSummary(filter)
      .then((response) => {
        dispatch(setAppToast(newToast("success")));
        setContributorSummary(response);
        setFilterCollapsed(true);
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  const generatePartnerReport = (filter: MatchReportPartnerFilter) => {
    setValidationErrors({});

    ApiService.matchReports
      .generatePartner(filter)
      .then((response) => {
        let htmlString = ReactDOMServer.renderToString(<PartnerHtml report={response} />);
        ApiService.common
          .generatePdfFromHtml({ html: htmlString, filename: "MatchReportPartner.pdf", landscape: false })
          .then((response) => {
            if (response) {
              var file = new Blob([response.data], { type: "application/pdf" });
              var fileURL = URL.createObjectURL(file);
              window.open(fileURL);
            }
          });
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  const generateAllPartnersReport = (filter: MatchReportAllPartnersFilter) => {
    setValidationErrors({});

    ApiService.matchReports
      .generateAllPartners(filter)
      .then((response) => {
        dispatch(setAppToast(newToast("success")));
        setAllPartners(response);
        setFilterCollapsed(true);
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  const generateTimeCardReport = (filter: MatchReportTimeCardFilter) => {
    setValidationErrors({});

    ApiService.matchReports
      .generateTimeCard(filter)
      .then((response) => {
        let htmlString = ReactDOMServer.renderToString(<TimeCardHtml report={response} />);
        ApiService.common
          .generatePdfFromHtml({ html: htmlString, filename: "MatchTimeCardReport.pdf", landscape: false })
          .then((response) => {
            if (response) {
              var file = new Blob([response.data], { type: "application/pdf" });
              var fileURL = URL.createObjectURL(file);
              window.open(fileURL);
            }
          });
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  const generateActualMatchReport = (filter: MatchReportActualMatchFilter) => {
    setValidationErrors({});

    ApiService.matchReports
      .generateActualMatch(filter)
      .then((response) => {
        dispatch(setAppToast(newToast("success")));
        setActualMatch(response);
        setFilterCollapsed(true);
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  const generateActualYearlyReport = (filter: MatchReportActualYearlyFilter) => {
    setValidationErrors({});

    ApiService.matchReports
      .generateActualYearly(filter)
      .then((response) => {
        dispatch(setAppToast(newToast("success")));
        setActualYearly(response);
        setFilterCollapsed(true);
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  return (
    <Page
      isForm
      header={
        <MatchReportPageHeader
          handle={matchReportPageRoute.handle}
          reportTypes={reportTypes}
          selectedReportType={selectedReportType}
          filtersCollapsed={filtersCollapsed}
          setFilterCollapsed={setFilterCollapsed}
          generateReport={generateReport}
        />
      }
    >
      <Grid ref={breakpointsRef} container spacing="lg" sx={{ minHeight: "100%" }}>
        {filtersCollapsed === false && (
          <Grid item xs={setBreakpoints({ md: 4, xs: 12 })}>
            <MatchReportPageFilters
              selectedReportType={selectedReportType}
              reportTypes={reportTypes}
              setSelectedReportType={setSelectedReportType}
              validationErrors={validationErrors}
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
              contributorSummaryFilter={contributorSummaryFilter}
              setContributorSummaryFilter={setContributorSummaryFilter}
              nonPersonnelDetailFilter={nonPersonnelDetailFilter}
              setNonPersonnelDetailFilter={setNonPersonnelDetailFilter}
              contributorDetailFilter={contributorDetailFilter}
              setContributorDetailFilter={setContributorDetailFilter}
              contributorFilter={contributorFilter}
              setContributorFilter={setContributorFilter}
              partnerFilter={partnerFilter}
              setPartnerFilter={setPartnerFilter}
              timeCardFilter={timeCardFilter}
              setTimeCardFilter={setTimeCardFilter}
              actualYearlyFilter={actualYearlyFilter}
              setActualYearlyFilter={setActualYearlyFilter}
              allPartnersFilter={allPartnersFilter}
              setAllPartnersFilter={setAllPartnersFilter}
              actualMatchFilter={actualMatchFilter}
              setActualMatchFilter={setActualMatchFilter}
            />
          </Grid>
        )}

        <Grid item xs={setBreakpoints({ xl: !filtersCollapsed ? 8 : 12, md: !filtersCollapsed ? 8 : 12, xs: 12 })}>
          <GeneratedReport
            selectedReportType={selectedReportType}
            reportTypes={reportTypes}
            contributorDetail={contributorDetail}
            nonPersonnelDetail={nonPersonnelDetail}
            contributorSummary={contributorSummary}
            allPartners={allPartners}
            actualYearly={actualYearly}
            actualMatch={actualMatch}
          />
        </Grid>
      </Grid>
    </Page>
  );
};
